<template>
    <!-- The content goes through DOMPurify first (see `sanitized`). -->
    <!-- eslint-disable vue/no-v-html -->
    <div
        :key="generation"
        ref="host"
        class="svg-editor-document h-full w-full [&>svg]:h-full [&>svg]:w-full"
        :class="{ 'svg-editor-document--animate': animating }"
        v-html="rendered"
    />
    <!-- eslint-enable vue/no-v-html -->
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import type { ShapeStyle } from "~/lib/svgEditor/lineWidth";
import type { OutlineWeight } from "~/lib/svgEditor/outline";
import type { Box } from "~/lib/svgEditor/selection";
import type { Point } from "~/lib/svgEditor/zoom";
import {
    documentBoxOf,
    shapeGeometryOf,
    shapeIndexIn,
    type ShapePath,
    shapePathOf,
    shapeSilhouetteOf,
    shapeStyleOf,
    shapeWeightOf,
    staggerAppearance,
} from "~/lib/svgEditor/dom";
import { someOutlinePoint } from "~/lib/svgEditor/outlineWalk";
import { createAttributePreview } from "~/lib/svgEditor/preview";
import {
    drawnShapes,
    sanitizeSvg,
    type ShapeEdit,
    type ShapeGeometry,
} from "~/lib/svgEditor/transform";

/**
 * What has been read of a shape as it stands — read once, asked for many
 * times while the shapes stay as they are: the selection's outline asks
 * for thousands of them with every change of the selection.
 */
type ShapeFacts = {
    geometry?: ShapeGeometry | undefined;
    path?: ShapePath | undefined;
    silhouette?: string | undefined;
};

/**
 * An SVG document shown inline, sanitized, fitted into its box like
 * `object-fit: contain`. With `animate`, its shapes grow in one after another
 * when the document first appears — a vectorized result then shows as the
 * vector objects it is made of instead of as a finished picture. An edited
 * document that follows appears as it is.
 *
 * The shapes are the editor's objects: a shape is identified by its index in
 * document order (`shapeIndexOf`); `shapeGeometry` says where it lies,
 * `shapeStyle` how it is painted, `shapeSilhouette` gives its bare
 * geometry, `shapePath` its path in the document's coordinates and
 * `shapeOutlineSome` looks for a point along it. A drag shows its edits on the shapes as a
 * preview, apart from the document (`previewAttributes`, `discardPreview`);
 * once the document has taken the edits over, the preview is committed
 * (`commitPreview`): the shapes stay as they are instead of being rendered
 * anew from the text, which takes long with thousands of them.
 */
const props = withDefaults(
    defineProps<{
        animate?: boolean;
        svg: string;
    }>(),
    { animate: true },
);

const emit = defineEmits<{
    /** The document's coordinate system (its viewBox), once it is in the DOM. */
    viewBox: [box: Box];
}>();

const host = ref<HTMLDivElement>();
const preview = createAttributePreview();
// The first document may grow in; a change (an edit, or a drag's preview)
// shows at once. Also, the finished animation keeps its `transform`, which
// would hide a shape's own transform written by an edit.
const changed = ref<boolean>(false);
// The drawn shapes of the root they were looked up in.
let shapeCache:
    { root: SVGSVGElement | undefined; shapes: Element[] } | undefined;
/** What has been read of the shapes as they stand. */
let facts = new WeakMap<Element, ShapeFacts>();
/** The document as it is rendered: sanitized. */
const rendered = ref<string>(sanitizeSvg(props.svg));
/** The document `rendered` was made from. */
let renderedFrom = props.svg;
/** The document the shapes already show, after a committed preview. */
let shown: string | undefined;
/**
 * Whether the shapes show more than `rendered`: edits committed from a
 * preview. Going back to `rendered` then has to put the shapes up anew,
 * although the markup is the same (see `generation`).
 */
let diverged = false;
/** Raised to put the shapes up anew from unchanged markup. */
const generation = ref<number>(0);

const animating = computed(() => props.animate && !changed.value);

/**
 * The document has taken the preview's edits over, as `svg`: the shapes
 * show it already and stay as they are.
 */
function commitPreview(svg: string) {
    preview.drop();
    forget();
    shown = svg;
    diverged = true;
    changed.value = true;
}

/** Puts the shapes back as they were before `previewAttributes`. */
function discardPreview() {
    preview.discard();
    forget();
}

/** A fact of `shape`, read by `read` once and kept until the shapes change. */
function factOf<Key extends keyof ShapeFacts>(
    shape: Element,
    key: Key,
    read: () => ShapeFacts[Key],
): ShapeFacts[Key] {
    let known = facts.get(shape);

    if (!known) {
        known = {};
        facts.set(shape, known);
    }

    if (!(key in known)) known[key] = read();

    return known[key];
}

/** The shapes changed: what was read of them is no longer so. */
function forget() {
    facts = new WeakMap();
}

function prepareShapes() {
    const svg = root();

    if (!svg) return;

    const box = documentBoxOf(svg);

    if (box) emit("viewBox", box);

    // The export's size attributes would otherwise make the SVG rigid.
    svg.removeAttribute("width");
    svg.removeAttribute("height");

    if (animating.value) staggerAppearance(shapes());
}

/**
 * Shows the edits' attributes on the shapes right away, without changing
 * the document — for a drag, as it goes.
 */
function previewAttributes(edits: ReadonlyMap<number, ShapeEdit>) {
    // The appearance's animation would keep its `transform` over the
    // preview's (see `changed`): the shapes would stay while their outline
    // moves. The first drag ends the appearance.
    changed.value = true;
    preview.apply(shapes(), edits);
    forget();
}

/**
 * The document `svg` was rendered as, sanitized, when it was `svg` that
 * was rendered — which spares an edit sanitizing it again.
 */
function renderedOf(svg: string): string | undefined {
    return svg === renderedFrom ? rendered.value : undefined;
}

function root(): SVGSVGElement | undefined {
    return host.value?.querySelector("svg") ?? undefined;
}

function shapeCount(): number {
    return shapes().length;
}

function shapeGeometry(index: number): ShapeGeometry | undefined {
    const shape = shapes().at(index);

    if (!shape) return;

    return factOf(shape, "geometry", () => shapeGeometryOf(shape, root()));
}

/** The index of the shape `target` is part of, if it is part of one. */
function shapeIndexOf(target: EventTarget | null): number | undefined {
    return shapeIndexIn(shapes(), target);
}

/**
 * Whether some point along the shape's outline, at most `spacing` apart in
 * the document's units, passes `test` — only those near `within` are
 * looked at, read from its path data as far as needed. Undefined for a
 * shape without path data.
 */
function shapeOutlineSome(
    index: number,
    spacing: number,
    within: Box | undefined,
    test: (point: Point) => boolean,
): boolean | undefined {
    const path = shapePath(index);

    if (!path) return;

    return someOutlinePoint(
        path.data,
        { matrix: path.matrix, spacing, within },
        test,
    );
}

/** The shape's geometry as path data in its own coordinates, and where that lies in the document. */
function shapePath(index: number): ShapePath | undefined {
    const shape = shapes().at(index);

    if (!shape) return;

    return factOf(shape, "path", () =>
        shapePathOf(shape, root(), shapeGeometry(index)),
    );
}

function shapes(): Element[] {
    if (!host.value) return [];

    // Every change of the document comes as new markup, with a new root;
    // until then the shapes stay the same elements. Looking them up anew
    // for every shape asked about would take long with many shapes.
    const svg = root();

    if (!shapeCache || shapeCache.root !== svg) {
        shapeCache = { root: svg, shapes: drawnShapes(host.value) };
    }

    return shapeCache.shapes;
}

function shapeSilhouette(index: number): string | undefined {
    const shape = shapes().at(index);

    if (!shape) return;

    return factOf(shape, "silhouette", () =>
        shapeSilhouetteOf(shape, root(), shapeGeometry(index)),
    );
}

function shapeStyle(index: number): ShapeStyle | undefined {
    return shapeStyleOf(shapes().at(index));
}

/** How much path data the shape holds. */
function shapeWeight(index: number): OutlineWeight {
    return shapeWeightOf(shapes().at(index));
}

onMounted(() => {
    void nextTick(prepareShapes);
});

// A document the shapes show already, from a committed preview, is not
// rendered again; any other is.
watch(
    () => props.svg,
    (svg) => {
        const alreadyShown = svg === shown;

        shown = undefined;

        if (alreadyShown) return;

        const markup = sanitizeSvg(svg);

        // The same markup would not be put up again: the shapes would keep
        // showing the committed edits.
        if (markup === rendered.value && diverged) generation.value++;

        rendered.value = markup;
        renderedFrom = svg;
        diverged = false;
    },
);

// A rendered document comes with new shapes: a preview on the old ones is
// over, and so is what was read of them.
watch([rendered, generation], () => {
    changed.value = true;
    preview.drop();
    forget();
    void nextTick(prepareShapes);
});

defineExpose({
    commitPreview,
    discardPreview,
    previewAttributes,
    renderedOf,
    shapeCount,
    shapeGeometry,
    shapeIndexOf,
    shapeOutlineSome,
    shapePath,
    shapeSilhouette,
    shapeStyle,
    shapeWeight,
});
</script>

<style scoped>
.svg-editor-document--animate :deep(svg) {
    animation: svg-editor-document-in 400ms ease-out both;
}

.svg-editor-document--animate :deep(path),
.svg-editor-document--animate :deep(polygon),
.svg-editor-document--animate :deep(polyline),
.svg-editor-document--animate :deep(rect),
.svg-editor-document--animate :deep(circle),
.svg-editor-document--animate :deep(ellipse) {
    transform-box: fill-box;
    transform-origin: center;
    animation: svg-editor-shape-in 650ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.svg-editor-document--animate :deep(.svg-editor-shape--placed) {
    animation-name: svg-editor-shape-fade-in;
}

@keyframes svg-editor-shape-fade-in {
    from {
        opacity: 0;
        filter: drop-shadow(0 0 6px rgb(19 135 236 / 0.9));
    }

    to {
        opacity: 1;
        filter: drop-shadow(0 0 0 transparent);
    }
}

@keyframes svg-editor-document-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes svg-editor-shape-in {
    0% {
        opacity: 0;
        transform: scale(0.6);
        filter: drop-shadow(0 0 6px rgb(19 135 236 / 0.9));
    }

    60% {
        opacity: 1;
        transform: scale(1.04);
    }

    100% {
        opacity: 1;
        transform: scale(1);
        filter: drop-shadow(0 0 0 transparent);
    }
}
</style>
